import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { ACCENT_CYAN, EASE_OUT, MUTED_GRAY, NOPING_GREEN, TEXT_LIGHT } from './constants';

export function Section({ children, className = '', id, bg }: { children: React.ReactNode; className?: string; id?: string; bg?: React.ReactNode }) {
  return (
    <section id={id} className={`relative px-4 py-20 sm:px-6 md:py-28 ${className}`}>
      {bg}
      <div className='relative z-10 mx-auto max-w-6xl'>{children}</div>
    </section>
  );
}

export function SectionTitle({ label, title, description }: { label: string; title: string; description?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.5, ease: EASE_OUT }} className='mb-14 text-center md:mb-16'>
      <span className='mb-3 inline-block font-mono text-xs font-semibold uppercase tracking-[0.25em]' style={{ color: ACCENT_CYAN }}>
        {label}
      </span>
      <h2 className='text-3xl font-bold sm:text-4xl md:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
        {title}
      </h2>
      {description && (
        <p className='mx-auto mt-3 max-w-xl text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
          {description}
        </p>
      )}
    </motion.div>
  );
}

export function Reveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} transition={{ duration: 0.5, delay, ease: EASE_OUT }} className={className}>
      {children}
    </motion.div>
  );
}

export function MetricBadge({ value, label, color = NOPING_GREEN }: { value: string; label: string; color?: string }) {
  return (
    <div className='inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-xs' style={{ color, backgroundColor: `${color}15`, border: `1px solid ${color}30` }}>
      <span className='size-1.5 rounded-full' style={{ backgroundColor: color }} />
      {value} {label}
    </div>
  );
}

export function StepConnector() {
  return (
    <div className='hidden w-16 items-center justify-center md:flex lg:w-24'>
      <div className='relative h-px w-full' style={{ background: `linear-gradient(to right, ${ACCENT_CYAN}60, ${NOPING_GREEN}60)` }}>
        <div className='absolute end-0 top-1/2 size-2 -translate-y-1/2 rotate-45 border-e-2 border-t-2' style={{ borderColor: `${NOPING_GREEN}60` }} />
      </div>
    </div>
  );
}
